import { Circle, CircleCheck } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { cn } from "@/core/utils/utils";
import {
  APPROVAL_GATES,
  type ApprovalGateHolder,
  describeMark,
  getApprovedGateCount,
  isApproved,
} from "./approval-types";

const MARK_SIZE = {
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
} as const;

interface ApprovalMarkProps {
  accessKey: ApprovalGateHolder;
  size?: keyof typeof MARK_SIZE;
  /**
   * Explain the mark on hover. On for the sheet tabs, where the mark stands
   * alone with nothing to name it; off everywhere it sits beside a label that
   * already does.
   */
  withTooltip?: boolean;
  className?: string;
}

/**
 * How far a unit has got, as one mark that reads as a progression: an empty grey
 * ring while nothing is signed off, then a BLUE icon of whichever gate has been
 * met, and finally a solid GREEN check once both are in.
 *
 * Grey to blue to green follows this app's own badge vocabulary — gray for not
 * started, blue for in progress, green for complete — so the colour is readable
 * before the shape is. Gold is deliberately avoided: it already means "manual"
 * on badges and carries the warning banner, so an amber halfway mark would read
 * as "something is wrong here" rather than "one of two steps done".
 *
 * Every state is a lucide glyph, including the empty one. A hand-rolled circle
 * looked wrong beside the others for two compounding reasons: drawn in a 20-unit
 * box against lucide's 24 it came out ~1px smaller, and its 3-unit stroke landed
 * near DOUBLE lucide's 2 — a small heavy dot next to fine-line icons. Sharing the
 * icon set keeps size, stroke and optical weight in step for free.
 */
export const ApprovalMark = ({
  accessKey,
  size = "sm",
  withTooltip = false,
  className,
}: ApprovalMarkProps) => {
  const approved = getApprovedGateCount(accessKey);
  const metGate = APPROVAL_GATES.find((gate) =>
    isApproved(accessKey, gate.type),
  );
  const glyphClass = cn("shrink-0", MARK_SIZE[size], className);

  const glyph =
    approved === APPROVAL_GATES.length ? (
      <CircleCheck
        aria-hidden="true"
        className={cn(glyphClass, "fill-primary text-primary-foreground")}
      />
    ) : metGate ? (
      <metGate.Icon
        aria-hidden="true"
        className={cn(glyphClass, "text-blue-60")}
      />
    ) : (
      // Still a mark, not nothing — it is how a sheet under approval is told
      // apart from one that was never in scope.
      <Circle
        aria-hidden="true"
        className={cn(glyphClass, "text-stroke-secondary")}
      />
    );

  if (!withTooltip) {
    return (
      <span data-testid="approval-mark" className="flex shrink-0 items-center">
        {glyph}
      </span>
    );
  }

  return (
    // Its own provider: the app's sidebar sets delayDuration to 0 app-wide, and
    // `disableHoverableContent` keeps the bubble from holding itself open under
    // the pointer — together those two made an earlier tooltip fire on the way
    // past and then sit over what the reader was looking at.
    <TooltipProvider delayDuration={200} disableHoverableContent>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* No aria-label here: this sits INSIDE the sheet tab's button, and a
              label on a child replaces the button's own accessible name — the
              tab started announcing as "Ready for PayPacks. The
              technicians can check..." instead of as its sheet. The tab adds the state as
              screen-reader text after its name instead. */}
          <span
            data-testid="approval-mark"
            className="flex shrink-0 items-center"
          >
            {glyph}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-64">
          {describeMark(accessKey)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
